import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { SITE } from '@/data/site';

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [keyword, setKeyword] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled || open
          ? 'bg-background-50/95 backdrop-blur border-b border-background-200'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 md:h-[68px] flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3 shrink-0 group">
          <span className="w-9 h-9 rounded-md bg-primary-500 text-background-50 flex items-center justify-center">
            <i className="ri-leaf-line text-xl" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading font-bold text-lg text-foreground-950 tracking-tight">
              {SITE.name}
            </span>
            <span className="text-[11px] tracking-[0.28em] text-primary-600 mt-0.5">
              {SITE.subName}
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="主导航">
          {SITE.nav.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `px-3.5 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
                  isActive
                    ? 'text-primary-600 font-medium bg-primary-50'
                    : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form
            onSubmit={submitSearch}
            className="hidden md:flex items-center gap-1.5 h-9 pl-3 pr-1.5 rounded-full border border-background-300 bg-background-50 focus-within:border-primary-400 transition-colors"
            role="search"
          >
            <span className="w-4 h-4 flex items-center justify-center text-foreground-500">
              <i className="ri-search-line text-base" />
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索话题或关键词"
              aria-label="站内搜索"
              className="w-32 xl:w-44 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 outline-none"
            />
            <button
              type="submit"
              className="h-7 px-3 rounded-full bg-primary-500 text-background-50 text-xs font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label="打开导航菜单"
            aria-expanded={open}
            className="lg:hidden w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-800 cursor-pointer"
          >
            <i className={open ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-4">
          <form onSubmit={submitSearch} className="md:hidden mb-3" role="search">
            <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-background-300">
              <span className="w-4 h-4 flex items-center justify-center text-foreground-500">
                <i className="ri-search-line text-base" />
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索话题或关键词"
                aria-label="站内搜索"
                className="flex-1 bg-transparent text-sm outline-none text-foreground-800 placeholder:text-foreground-400"
              />
            </div>
          </form>
          <nav className="flex flex-col" aria-label="移动端导航">
            {SITE.nav.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `px-3 py-3 text-sm rounded-md ${
                    isActive
                      ? 'text-primary-600 font-medium bg-primary-50'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}